import type { CameraSpec } from '../services/cameraLayoutService';
import type { PoolBounds } from './cameraGridPlacement';
import {
  DEFAULT_WALL_TILT_DEG,
  wallCameraToPoolGapM,
  wallOpticalCenterMeters,
} from './cameraGridPlacement';
import {
  applyCameraOptics,
  DEFAULT_FOCAL_LENGTH_MM,
  DEFAULT_SENSOR_WIDTH_MM,
  focalFromVerticalFovDeg,
} from './cameraOptics';

const MIN_FOCAL_MM = 1.8;
const MAX_FOCAL_MM = 16;
/** 격자 셀 경계 안쪽으로 살짝 넣어 이웃 셀과 겹침 방지 */
const CELL_INSET = 0.98;
/** 벽/비격자: 인접 카메라 거리 기준 */
const FOOTPRINT_SEPARATION = 0.92;

/**
 * 벽 커버리지 프로파일.
 * - baseline_20260722: 2026-07-22 「원래대로」— 공통 화각·넓은 담당·단순 조준
 * - preset_1_20260722: 사용자 지정 「1번」 스냅샷(2026-07-22 17:51 전후, 커버 ~91%, 겹침 5대)
 * - balanced_20260722: 시뮬 기준 커버·겹침 균형(기본 자동 배치)
 */
export type WallCoverageProfile =
  | 'baseline_20260722'
  | 'preset_1_20260722'
  | 'reduced_overlap'
  | 'balanced_20260722';

/** 활성 프로파일 */
let activeWallCoverageProfile: WallCoverageProfile = 'balanced_20260722';

type WallProfileParams = {
  nearDepthFrac: number;
  alongInset: number;
  nearAimFrac: number;
  wallPerCellFov: boolean;
  perpendicularHeading: boolean;
  alongCamWeight: number;
  edgeAlongPull: number;
  centerCellPanFrac: number;
  /** 자동 배치 시 H/V 상한(°), null이면 제한 없음 — 중앙 셀 */
  maxWallHfovDeg: number | null;
  /** 양끝 셀 H-FOV 상한(°), null이면 maxWallHfovDeg만 사용 */
  maxWallHfovDegEdge: number | null;
  maxWallVfovDeg: number | null;
  maxRangeScale: number;
  /** max_range 축소 후 하한(m) */
  minWallRangeM: number;
  neighborFovCap: boolean;
};

/** 「1번」 스냅샷 파라미터 (사용자 2026-07-22 화면 고정) */
const PRESET_1_WALL_PARAMS: WallProfileParams = {
  nearDepthFrac: 0.46,
  alongInset: 0.82,
  nearAimFrac: 0.26,
  wallPerCellFov: true,
  perpendicularHeading: false,
  alongCamWeight: 0.4,
  edgeAlongPull: 0.28,
  centerCellPanFrac: 0.14,
  maxWallHfovDeg: null,
  maxWallHfovDegEdge: null,
  maxWallVfovDeg: null,
  maxRangeScale: 1,
  minWallRangeM: 0,
  neighborFovCap: false,
};

/**
 * 균형안: 모서리·가장자리·중앙 이음 + 겹침 억제
 */
const BALANCED_WALL_SEAM_OVERLAP = 1.12;
/** 조준 깊이는 얕게 — 가장자리부터 시야가 시작되도록 */
const BALANCED_CENTER_AIM_FRAC = 0.22;
const BALANCED_CENTER_FAR_FRAC = 0.54;
const BALANCED_EDGE_AIM_FRAC = 0.28;
const BALANCED_EDGE_FAR_FRAC = 0.6;
/** 맞은편 벽캠이 없을 때(좌만·우만 등) — 풀 반대쪽 끝까지 */
const BALANCED_SOLE_SIDE_FAR_FRAC = 0.97;
const BALANCED_NEAR_EDGE_FRAC = 0.005;

/**
 * 벽캠 틸트 허용 밴드.
 * 30°는 권장 상한일 뿐 고정값이 아님. 이전엔 상한=30이라 H≈3.5m+에서
 * 자동 계산이 전부 30°로 포화됨 → 기하 기반 값이 살아나도록 여유를 둠.
 * 45°+ 탑뷰는 여전히 불리하므로 하드 상한은 38°.
 */
const WALL_LABEL_TILT_MIN = 15;
const WALL_LABEL_TILT_MAX = 38;
/** UI·도움말에 쓰는 권장 상한 */
export const WALL_TILT_RECOMMENDED_MAX = 35;
export const WALL_TILT_RECOMMENDED_MIN = 18;
/** 근거리(가장자리) 쪽으로 틸트 가중·가파름 보정(°) — 과한 포화 완화 */
const WALL_NEAR_EDGE_TILT_BIAS = 0.5;
const WALL_NEAR_EDGE_TILT_NUDGE = 0.8;

const BALANCED_WALL_PARAMS: WallProfileParams = {
  nearDepthFrac: 0.52,
  alongInset: 0.97,
  nearAimFrac: 0.22,
  wallPerCellFov: true,
  perpendicularHeading: false,
  alongCamWeight: 0.5,
  edgeAlongPull: 0.28,
  centerCellPanFrac: 0,
  maxWallHfovDeg: 76,
  maxWallHfovDegEdge: 112,
  maxWallVfovDeg: 68,
  maxRangeScale: 1,
  minWallRangeM: 0,
  neighborFovCap: true,
};

export function getWallCoverageProfile(): WallCoverageProfile {
  return activeWallCoverageProfile;
}

export function setWallCoverageProfile(profile: WallCoverageProfile): void {
  activeWallCoverageProfile = profile;
}

/**
 * backend `ground_point_visible` / `footprint_polygon_norm` 과 맞춘 max_range 하한(m).
 * 조준 바닥점 + V-FOV 하단까지의 광선 길이(보수적).
 */
export function wallMaxRangeFromGroundAim(
  ex: number,
  ey: number,
  heightM: number,
  aimX: number,
  aimY: number,
  vfovDeg: number,
  poolLengthM: number,
  poolWidthM: number,
): number {
  const h = Math.max(0.5, heightM);
  const horiz = Math.max(0.4, Math.hypot(aimX - ex, aimY - ey));
  const slantAim = Math.hypot(h, horiz);
  const vHalfRad = (Math.max(15, Math.min(110, vfovDeg)) * Math.PI) / 180 / 2;
  const horizFar = horiz + h * Math.tan(vHalfRad) * 0.72;
  const slantFar = Math.hypot(h, horizFar);
  const need = Math.max(slantAim, slantFar) * 1.05;
  const poolCap = Math.hypot(poolLengthM, poolWidthM) * 1.15;
  // 하한 11m는 좁은 풀에서 맞은편까지 뚫어 겹침이 커지므로 쓰지 않음
  return Math.min(45, Math.max(5, Math.min(need, poolCap)));
}

function wallProfileParams(): WallProfileParams {
  if (activeWallCoverageProfile === 'baseline_20260722') {
    return {
      nearDepthFrac: 0.52,
      alongInset: CELL_INSET,
      nearAimFrac: 0.28,
      wallPerCellFov: false,
      perpendicularHeading: false,
      alongCamWeight: 0.35,
      edgeAlongPull: 0.14,
      centerCellPanFrac: 0,
      maxWallHfovDeg: null,
      maxWallHfovDegEdge: null,
      maxWallVfovDeg: null,
      maxRangeScale: 1,
      minWallRangeM: 0,
      neighborFovCap: false,
    };
  }
  if (activeWallCoverageProfile === 'balanced_20260722') {
    return { ...BALANCED_WALL_PARAMS };
  }
  return { ...PRESET_1_WALL_PARAMS };
}

function xyMeters(cam: Partial<CameraSpec>, poolLengthM: number, poolWidthM: number) {
  return {
    x: (Number(cam.x ?? 50) / 100) * poolLengthM,
    y: (Number(cam.y ?? 50) / 100) * poolWidthM,
  };
}

function focalFromHorizontalFovDeg(hfovDeg: number, sensorWidthMm: number): number {
  const rad = (hfovDeg * Math.PI) / 180;
  return sensorWidthMm / (2 * Math.tan(rad / 2));
}

function groundRadiusM(hfovDeg: number, heightM: number): number {
  return heightM * Math.tan((hfovDeg * Math.PI) / 180 / 2);
}

function focalForCellCoverage(
  halfW: number,
  halfH: number,
  heightM: number,
  sensorW: number,
  resolutionW: number,
  resolutionH: number,
): number {
  const h = Math.max(0.5, heightM);
  let hfovW = (2 * Math.atan(halfW / h) * 180) / Math.PI;
  let hfovH = (2 * Math.atan(halfH / h) * 180) / Math.PI;
  hfovW = Math.max(22, Math.min(110, hfovW));
  hfovH = Math.max(15, Math.min(110, hfovH));
  const aspect = resolutionH / Math.max(resolutionW, 1);
  const hfovFromV = (2 * Math.atan(Math.tan((hfovH * Math.PI) / 180 / 2) / aspect) * 180) / Math.PI;
  const hfovNeed = Math.max(hfovW, hfovFromV);
  return focalFromHorizontalFovDeg(Math.min(110, hfovNeed), sensorW);
}

function cellHalfSpanM(
  bounds: PoolBounds,
  rows: number,
  cols: number,
  poolLengthM: number,
  poolWidthM: number,
): { halfW: number; halfH: number } {
  const spanX = ((bounds.maxX - bounds.minX) / 100) * poolLengthM;
  const spanY = ((bounds.maxY - bounds.minY) / 100) * poolWidthM;
  return {
    halfW: (spanX / Math.max(1, cols) / 2) * CELL_INSET,
    halfH: (spanY / Math.max(1, rows) / 2) * CELL_INSET,
  };
}

type WallSide = 'top' | 'bottom' | 'left' | 'right' | 'inside';

/** 도면상 벽 카메라가 어느 변에 가까운지 — 가장 가까운 변 1개만 (구석도 장변 유지) */
function detectWallSide(p: { x: number; y: number }, poolLengthM: number, poolWidthM: number): WallSide {
  const L = Math.max(poolLengthM, 0.01);
  const W = Math.max(poolWidthM, 0.01);
  const dTop = Math.max(0, p.y);
  const dBot = Math.max(0, W - p.y);
  // 단변에 미세 가산 → 동률·거의 동률이면 위·아래(장변) 우선
  const dLeft = Math.max(0, p.x) + 1e-3;
  const dRight = Math.max(0, L - p.x) + 1e-3;
  const ranked: { side: WallSide; d: number }[] = [
    { side: 'top', d: dTop },
    { side: 'bottom', d: dBot },
    { side: 'left', d: dLeft },
    { side: 'right', d: dRight },
  ];
  ranked.sort((a, b) => a.d - b.d);
  return ranked[0]?.side ?? 'inside';
}

/** 배치 이름에 벽 방향이 있으면 그걸 우선 (유동 재배치 시 위3/아래3 유지) */
function wallSideFromCamName(name: string | undefined | null): WallSide | null {
  const s = String(name || '');
  if (s.includes('윗')) return 'top';
  if (s.includes('아랫')) return 'bottom';
  if (s.includes('왼쪽')) return 'left';
  if (s.includes('오른쪽')) return 'right';
  return null;
}

/**
 * 벽 CCTV 담당 구역:
 * - along: 벽면을 따라 맡은 폭의 절반
 * - into: 설치 벽 쪽 근거리 깊이
 */
function wallCoverageSpanM(
  side: WallSide,
  rows: number,
  cols: number,
  poolLengthM: number,
  poolWidthM: number,
): { halfAlong: number; intoDepth: number } {
  const r = Math.max(1, rows);
  const c = Math.max(1, cols);
  const { nearDepthFrac, alongInset } = wallProfileParams();
  if (side === 'top' || side === 'bottom') {
    return {
      halfAlong: (poolLengthM / c / 2) * alongInset,
      intoDepth: poolWidthM * nearDepthFrac * CELL_INSET,
    };
  }
  if (side === 'left' || side === 'right') {
    return {
      halfAlong: (poolWidthM / r / 2) * alongInset,
      intoDepth: poolLengthM * nearDepthFrac * CELL_INSET,
    };
  }
  return {
    halfAlong: (Math.min(poolLengthM, poolWidthM) / 4) * alongInset,
    intoDepth: (Math.min(poolLengthM, poolWidthM) / 2) * CELL_INSET * nearDepthFrac,
  };
}

/** 0=중앙 셀, 1=양끝 셀 — 비스듬 조준 강도 */
function wallCellEdgeWeight(
  side: WallSide,
  col: number,
  cols: number,
  row: number,
  rows: number,
): number {
  const c = Math.max(1, cols);
  const r = Math.max(1, rows);
  if (side === 'top' || side === 'bottom') {
    if (c <= 1) return 0;
    return Math.abs(col / (c - 1) - 0.5) * 2;
  }
  if (side === 'left' || side === 'right') {
    if (r <= 1) return 0;
    return Math.abs(row / (r - 1) - 0.5) * 2;
  }
  return 0;
}

/** 셀별 화각 계산용 담당 반폭(양끝·중앙 차등) */
function wallCellFocalHalfM(
  side: WallSide,
  col: number,
  cols: number,
  row: number,
  rows: number,
  poolLengthM: number,
  poolWidthM: number,
): { halfAlong: number; halfInto: number } {
  const { halfAlong, intoDepth } = wallCoverageSpanM(
    side,
    rows,
    cols,
    poolLengthM,
    poolWidthM,
  );
  const edgeW = wallCellEdgeWeight(side, col, cols, row, rows);
  const balanced = activeWallCoverageProfile === 'balanced_20260722';
  const alongScale = balanced
    ? edgeW < 0.2
      ? side === 'left' || side === 'right'
        ? 1.15 // 좌·우 단독: 짧은 변 전체
        : 1.0 // 중앙 장변: 열만 — 겹침↓
      : 1.18 + 0.28 * edgeW // 측면: 모서리까지
    : 0.86 + 0.14 * (1 - edgeW);
  const intoScale = balanced
    ? edgeW < 0.2
      ? side === 'left' || side === 'right'
        ? 1.05
        : 1.05 // 위·아래 중앙도 근거리 깊이 확보
      : 1.08 + 0.1 * edgeW
    : 0.88 + 0.12 * edgeW;
  return {
    halfAlong: halfAlong * alongScale,
    halfInto: (intoDepth / 2) * intoScale,
  };
}

function isCenterAlongCell(index: number, count: number): boolean {
  return count >= 3 && index === Math.floor(count / 2);
}

/**
 * 맞은편 과침투 억제(측면용 밴드).
 * 중앙·측면 본 포즈는 poseCoveringPoolEdgeToFar 사용.
 */
function balancedWallRangeBandM(
  side: WallSide,
  poolLengthM: number,
  poolWidthM: number,
  heightM: number,
  wallOffsetM = 0.8,
  centerAim = false,
): { min: number; max: number } {
  const h = Math.max(0.5, heightM);
  const depth =
    side === 'top' || side === 'bottom'
      ? Math.max(poolWidthM, 1)
      : side === 'left' || side === 'right'
        ? Math.max(poolLengthM, 1)
        : Math.min(poolLengthM, poolWidthM);
  const off = Math.max(0, wallOffsetM);
  const horizMin = (centerAim ? depth * 0.4 : depth * 0.45) + off;
  const horizMax =
    (centerAim ? depth * BALANCED_CENTER_FAR_FRAC : depth * BALANCED_EDGE_FAR_FRAC) +
    off;
  return {
    min: Math.min(36, Math.hypot(h, horizMin) * 1.0),
    max: Math.min(40, Math.hypot(h, horizMax) * 1.0),
  };
}

/** 풀 깊이 방향 원거리 바닥점(along은 카메라 열 위치 유지) */
function wallFarGroundPoint(
  side: WallSide,
  farFrac: number,
  poolLengthM: number,
  poolWidthM: number,
  alongX: number,
  alongY: number,
): { fx: number; fy: number } {
  // 단독 벽면은 0.97까지 허용(맞은편 끝까지)
  const f = Math.max(0.2, Math.min(0.98, farFrac));
  if (side === 'top') return { fx: alongX, fy: poolWidthM * f };
  if (side === 'bottom') return { fx: alongX, fy: poolWidthM * (1 - f) };
  if (side === 'left') return { fx: poolLengthM * f, fy: alongY };
  if (side === 'right') return { fx: poolLengthM * (1 - f), fy: alongY };
  return { fx: poolLengthM / 2, fy: poolWidthM / 2 };
}

type WallSideCounts = { top: number; bottom: number; left: number; right: number };

function countWallSides(
  cameras: CameraSpec[],
  poolLengthM: number,
  poolWidthM: number,
): WallSideCounts {
  const counts: WallSideCounts = { top: 0, bottom: 0, left: 0, right: 0 };
  for (const cam of cameras) {
    if (cam.mount_type !== 'wall') continue;
    const p = xyMeters(cam, poolLengthM, poolWidthM);
    const side = wallSideFromCamName(cam.name) || detectWallSide(p, poolLengthM, poolWidthM);
    if (side === 'inside') continue;
    counts[side] += 1;
  }
  return counts;
}

/** 맞은편에 벽캠이 없으면 풀 끝까지, 있으면 중앙 이음.
 *  천장캠이 있으면 벽은 가장자리·근중반만(중앙은 천장 담당). */
function balancedFarFracForSide(
  side: WallSide,
  isCenter: boolean,
  counts: WallSideCounts,
  hasCeiling: boolean,
): number {
  if (hasCeiling) {
    if (side === 'left' || side === 'right') {
      return isCenter ? 0.34 : 0.4;
    }
    return isCenter ? 0.36 : 0.42;
  }
  if (side === 'left' && counts.right <= 0) return BALANCED_SOLE_SIDE_FAR_FRAC;
  if (side === 'right' && counts.left <= 0) return BALANCED_SOLE_SIDE_FAR_FRAC;
  if (side === 'top' && counts.bottom <= 0) return BALANCED_SOLE_SIDE_FAR_FRAC;
  if (side === 'bottom' && counts.top <= 0) return BALANCED_SOLE_SIDE_FAR_FRAC;
  return isCenter ? BALANCED_CENTER_FAR_FRAC : BALANCED_EDGE_FAR_FRAC;
}

/** 풀 가장자리(바로 안쪽) — 근거리 시야 목표 */
function wallNearGroundPoint(
  side: WallSide,
  poolLengthM: number,
  poolWidthM: number,
  alongX: number,
  alongY: number,
): { nx: number; ny: number } {
  const e = BALANCED_NEAR_EDGE_FRAC;
  if (side === 'top') return { nx: alongX, ny: poolWidthM * e };
  if (side === 'bottom') return { nx: alongX, ny: poolWidthM * (1 - e) };
  if (side === 'left') return { nx: poolLengthM * e, ny: alongY };
  if (side === 'right') return { nx: poolLengthM * (1 - e), ny: alongY };
  return { nx: poolLengthM / 2, ny: poolWidthM / 2 };
}

/**
 * 풀 가장자리(근) ~ 목표 원거리까지 한 V-FOV로 덮도록 틸트·필요 V·시야거리 계산.
 * 기존에 원거리만 맞추면 근거리(가장자리)가 비는 문제를 해결.
 */
function poseCoveringPoolEdgeToFar(
  ex: number,
  ey: number,
  heightM: number,
  nearX: number,
  nearY: number,
  farX: number,
  farY: number,
  maxVfovDeg: number,
): {
  heading_deg: number;
  tilt_deg: number;
  max_range_m: number;
  vfov_deg: number;
} {
  const h = Math.max(0.5, heightM);
  const horizNear = Math.max(0.35, Math.hypot(nearX - ex, nearY - ey));
  const horizFar = Math.max(horizNear + 0.4, Math.hypot(farX - ex, farY - ey));
  let heading = (Math.atan2(farY - ey, farX - ex) * 180) / Math.PI;
  heading = Math.round((((heading % 360) + 360) % 360) * 10) / 10;

  const angNear = (Math.atan2(h, horizNear) * 180) / Math.PI; // 가파름(가까움)
  const angFar = (Math.atan2(h, horizFar) * 180) / Math.PI; // 완만(멀리)
  let vfov = Math.max(18, angNear - angFar);
  const vCap = Math.max(28, Math.min(75, maxVfovDeg));
  // 근·원 밴드 중간 + 가장자리 약간 가중 (전부 상한으로 몰리지 않게)
  let tilt =
    angNear * WALL_NEAR_EDGE_TILT_BIAS + angFar * (1 - WALL_NEAR_EDGE_TILT_BIAS);
  if (vfov > vCap) {
    // V 상한 시: 풀 가장자리(근거리)를 FOV 상단에 맞춤
    vfov = vCap;
    tilt = angNear - vfov / 2;
  } else {
    // 실제 V로 근거리를 FOV 상단 쪽에 두되, 원거리 밴드도 유지
    const tiltForNear = angNear - vfov / 2;
    const tiltMid = (angNear + angFar) / 2;
    tilt = tiltForNear * 0.45 + tiltMid * 0.55 + WALL_NEAR_EDGE_TILT_NUDGE;
  }
  tilt = Math.round(Math.max(WALL_LABEL_TILT_MIN, Math.min(WALL_LABEL_TILT_MAX, tilt)) * 10) / 10;
  vfov = Math.round(Math.max(18, Math.min(vCap, vfov)) * 10) / 10;
  const max_range_m = Math.round(Math.hypot(h, horizFar) * 1.04 * 10) / 10;
  return { heading_deg: heading, tilt_deg: tilt, max_range_m, vfov_deg: vfov };
}

function wallBalancedNearAimFrac(
  side: WallSide,
  col: number,
  cols: number,
  row: number,
  rows: number,
  baseFrac: number,
): number {
  if (activeWallCoverageProfile !== 'balanced_20260722') return baseFrac;
  const alongIdx = side === 'top' || side === 'bottom' ? col : row;
  const alongCount = side === 'top' || side === 'bottom' ? cols : rows;
  if (isCenterAlongCell(alongIdx, alongCount)) return BALANCED_CENTER_AIM_FRAC;
  return BALANCED_EDGE_AIM_FRAC;
}

/** 벽면 → 풀 바닥 목표점(담당 셀 · 근거리 깊이) */
function wallGroundAimPoint(
  side: WallSide,
  col: number,
  cols: number,
  row: number,
  rows: number,
  poolLengthM: number,
  poolWidthM: number,
  camXY?: { x: number; y: number },
): { tx: number; ty: number } {
  const c = Math.max(1, cols);
  const r = Math.max(1, rows);
  const { nearAimFrac, alongCamWeight, edgeAlongPull, centerCellPanFrac } =
    wallProfileParams();
  const cx = poolLengthM / 2;
  const cy = poolWidthM / 2;
  const cellAlongX = poolLengthM * ((col + 0.5) / c);
  const cellAlongY = poolWidthM * ((row + 0.5) / r);
  const w = camXY ? Math.max(0, Math.min(1, alongCamWeight)) : 0;
  let alongX = camXY ? camXY.x * w + cellAlongX * (1 - w) : cellAlongX;
  let alongY = camXY ? camXY.y * w + cellAlongY * (1 - w) : cellAlongY;
  const edgeW = wallCellEdgeWeight(side, col, c, row, r);
  // 측면만 풀 가운데로 팬 (바깥쪽 편향 없음 — 끝단은 H-FOV로 커버)
  const pull = edgeAlongPull * edgeW;

  if (side === 'top' || side === 'bottom') {
    alongX += (cx - alongX) * pull;
    if (centerCellPanFrac > 0 && isCenterAlongCell(col, c)) {
      const pan = (poolLengthM / c) * centerCellPanFrac;
      alongX += side === 'top' ? pan : -pan;
    }
    const aimFrac = wallBalancedNearAimFrac(side, col, c, row, r, nearAimFrac);
    const ty =
      side === 'top' ? poolWidthM * aimFrac : poolWidthM * (1 - aimFrac);
    return { tx: alongX, ty };
  }
  if (side === 'left' || side === 'right') {
    alongY += (cy - alongY) * pull;
    if (centerCellPanFrac > 0 && isCenterAlongCell(row, r)) {
      const pan = (poolWidthM / r) * centerCellPanFrac;
      alongY += side === 'left' ? pan : -pan;
    }
    const aimFrac = wallBalancedNearAimFrac(side, col, c, row, r, nearAimFrac);
    const tx =
      side === 'left' ? poolLengthM * aimFrac : poolLengthM * (1 - aimFrac);
    return { tx, ty: alongY };
  }
  return { tx: cx, ty: cy };
}

/** 벽면에 수직인 방위(도면: 0=+x, 90=+y) */
function wallPerpendicularHeadingDeg(side: WallSide): number {
  if (side === 'top') return 90;
  if (side === 'bottom') return 270;
  if (side === 'left') return 0;
  if (side === 'right') return 180;
  return 90;
}

function wallCellIndex(
  side: WallSide,
  p: { x: number; y: number },
  rows: number,
  cols: number,
  poolLengthM: number,
  poolWidthM: number,
): { row: number; col: number } {
  const r = Math.max(1, rows);
  const c = Math.max(1, cols);
  if (side === 'top') {
    const t = Math.max(0, Math.min(1, p.x / Math.max(poolLengthM, 0.01)));
    return { row: 0, col: Math.min(c - 1, Math.floor(t * c)) };
  }
  if (side === 'bottom') {
    const t = Math.max(0, Math.min(1, p.x / Math.max(poolLengthM, 0.01)));
    return { row: r - 1, col: Math.min(c - 1, Math.floor(t * c)) };
  }
  if (side === 'left') {
    const t = Math.max(0, Math.min(1, p.y / Math.max(poolWidthM, 0.01)));
    return { row: Math.min(r - 1, Math.floor(t * r)), col: 0 };
  }
  if (side === 'right') {
    const t = Math.max(0, Math.min(1, p.y / Math.max(poolWidthM, 0.01)));
    return { row: Math.min(r - 1, Math.floor(t * r)), col: c - 1 };
  }
  return { row: 0, col: 0 };
}

/** 벽 설치 오프셋 반영 광학 중심(m) — 벽면 수직 바깥 */
function effectiveWallXYM(
  p: { x: number; y: number },
  poolLengthM: number,
  poolWidthM: number,
  edgeM: number,
  wallM: number,
  sideHint?: WallSide | null,
): { x: number; y: number } {
  const side = sideHint && sideHint !== 'inside' ? sideHint : null;
  return wallOpticalCenterMeters(
    p.x,
    p.y,
    poolLengthM,
    poolWidthM,
    edgeM,
    wallM,
    side,
  );
}

/** 바닥 목표점을 향하는 방위·하향각 */
function poseTowardGroundTarget(
  cx: number,
  cy: number,
  heightM: number,
  tx: number,
  ty: number,
  vfovDeg: number,
  fillCoverage: boolean,
): { heading_deg: number; tilt_deg: number } {
  const dx = tx - cx;
  const dy = ty - cy;
  const horiz = Math.max(0.8, Math.hypot(dx, dy));
  let heading = (Math.atan2(dy, dx) * 180) / Math.PI;
  heading = Math.round((((heading % 360) + 360) % 360) * 10) / 10;
  let tilt = (Math.atan2(heightM, horiz) * 180) / Math.PI;
  if (fillCoverage) {
    tilt += Math.min(4, vfovDeg * 0.06) + WALL_NEAR_EDGE_TILT_NUDGE;
  }
  tilt = Math.round(
    Math.max(WALL_LABEL_TILT_MIN, Math.min(WALL_LABEL_TILT_MAX, tilt)) * 10,
  ) / 10;
  return { heading_deg: heading, tilt_deg: tilt };
}

/** 벽면 → 설치 벽 쪽 근거리(자벽 기준)를 보도록 방위 */
function wallFacingHeadingDeg(
  side: WallSide,
  p: { x: number; y: number },
  poolLengthM: number,
  poolWidthM: number,
): number {
  if (wallProfileParams().perpendicularHeading) {
    return wallPerpendicularHeadingDeg(side);
  }
  let tx = poolLengthM / 2;
  let ty = poolWidthM / 2;
  const nearFrac = wallProfileParams().nearAimFrac;
  if (side === 'top') {
    tx = p.x;
    ty = poolWidthM * nearFrac;
  } else if (side === 'bottom') {
    tx = p.x;
    ty = poolWidthM * (1 - nearFrac);
  } else if (side === 'left') {
    tx = poolLengthM * nearFrac;
    ty = p.y;
  } else if (side === 'right') {
    tx = poolLengthM * (1 - nearFrac);
    ty = p.y;
  }
  const deg = (Math.atan2(ty - p.y, tx - p.x) * 180) / Math.PI;
  return Math.round((((deg % 360) + 360) % 360) * 10) / 10;
}

/** 광학축이 설치 벽 쪽 근거리 목표점(수평거리)에 닿도록 하향각 */
function wallTiltTowardDepthM(heightM: number, intoDepthM: number): number {
  const aim = Math.max(1.2, intoDepthM * 0.55);
  const tilt = (Math.atan2(heightM, aim) * 180) / Math.PI;
  return Math.round(
    Math.max(WALL_LABEL_TILT_MIN, Math.min(WALL_LABEL_TILT_MAX, tilt)) * 10,
  ) / 10;
}

export type AutoOptimizeOptions = {
  defaultFocalMm?: number;
  /** 상단 공통 H/V-FOV — 잠금 없는 CCTV는 이 값으로 초점·시야 계산 */
  defaultFovHDeg?: number;
  defaultFovVDeg?: number;
  ceilingGrid?: { rows: number; cols: number; bounds: PoolBounds };
  wallGrid?: { rows: number; cols: number; bounds: PoolBounds };
  /** 자동 배치: 격자 셀을 빈틈 없이 덮도록 화각 맞춤 */
  fillCoverage?: boolean;
  /**
   * ON이면 벽캠 설치 위치를 등간격(정비례)에 묶지 않고,
   * 양끝·중앙 커버를 위해 벽면 따라 간격을 재배치.
   */
  flexInstallSpacing?: boolean;
  lockedFocalIndices?: ReadonlySet<number>;
};

/**
 * 유동 조정 ON일 때만 사용. 등간격보다 끝단에 조금 더 가깝게 (과한 쏠림 방지).
 * 예: 3대·25.2m → 약 3.5 / 12.6 / 21.7 (끝단 여유와 카메라 간격 균형).
 */
function edgeBiasedAlongFractions(count: number): number[] {
  const n = Math.max(1, Math.floor(count));
  if (n === 1) return [0.5];
  const out: number[] = [];
  for (let i = 0; i < n; i++) {
    const tEq = (i + 0.5) / n;
    const tCheb = 0.5 - 0.5 * Math.cos((Math.PI * (i + 0.5)) / n);
    // 등간격 비중을 높여 끝단이 과도하게 붙지 않게 (이전 0.08/0.92 → 0.55/0.45)
    let t = tEq * 0.55 + tCheb * 0.45;
    const inset = Math.min(0.015, 0.05 / n);
    t = inset + t * (1 - 2 * inset);
    out.push(t);
  }
  return out.sort((a, b) => a - b);
}

/** 천장 격자 카메라를 셀 중앙보다 모서리 쪽으로 살짝 당겨 구석 커버 보강 */
function redistributeCeilingInstallPositions(
  cameras: CameraSpec[],
  poolLengthM: number,
  poolWidthM: number,
  grid?: { rows: number; cols: number; bounds?: PoolBounds },
): CameraSpec[] {
  const rows = grid?.rows ?? 0;
  const cols = grid?.cols ?? 0;
  if (rows <= 0 || cols <= 0) return cameras;
  const b = grid?.bounds ?? { minX: 0, maxX: 100, minY: 0, maxY: 100 };
  const spanX = Math.max(1e-6, b.maxX - b.minX);
  const spanY = Math.max(1e-6, b.maxY - b.minY);
  const xFracs = edgeBiasedAlongFractions(cols);
  const yFracs = edgeBiasedAlongFractions(rows);
  const next = cameras.map((c) => ({ ...c }));
  const ceilingIdx = next
    .map((c, i) => (c.mount_type === 'wall' ? -1 : i))
    .filter((i) => i >= 0);
  if (ceilingIdx.length !== rows * cols) return cameras;
  // 행 우선(위→아래), 열(좌→우) — buildCamerasFromGrid 와 동일
  let k = 0;
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const i = ceilingIdx[k++];
      const alongXM = Math.round(xFracs[col] * poolLengthM * 10) / 10;
      const alongYM = Math.round(yFracs[row] * poolWidthM * 10) / 10;
      next[i].x = Math.round((b.minX + (alongXM / Math.max(poolLengthM, 0.01)) * spanX) * 10) / 10;
      next[i].y = Math.round((b.minY + (alongYM / Math.max(poolWidthM, 0.01)) * spanY) * 10) / 10;
    }
  }
  return next;
}

/** 같은 벽의 카메라들을 유동 간격으로 재배치(정규좌표 %). */
function redistributeWallInstallPositions(
  cameras: CameraSpec[],
  poolLengthM: number,
  poolWidthM: number,
  bounds?: PoolBounds,
): CameraSpec[] {
  const b = bounds ?? { minX: 0, maxX: 100, minY: 0, maxY: 100 };
  const spanX = Math.max(1e-6, b.maxX - b.minX);
  const spanY = Math.max(1e-6, b.maxY - b.minY);

  type Item = { index: number; along: number };
  const bySide: Record<string, Item[]> = {
    top: [],
    bottom: [],
    left: [],
    right: [],
  };

  cameras.forEach((cam, index) => {
    if (cam.mount_type !== 'wall') return;
    const p = xyMeters(cam, poolLengthM, poolWidthM);
    const side = wallSideFromCamName(cam.name) || detectWallSide(p, poolLengthM, poolWidthM);
    if (side === 'inside') return;
    const along =
      side === 'top' || side === 'bottom' ? Number(cam.x) || 50 : Number(cam.y) || 50;
    bySide[side].push({ index, along });
  });

  const next = cameras.map((c) => ({ ...c }));
  (['top', 'bottom', 'left', 'right'] as const).forEach((side) => {
    const group = bySide[side];
    if (group.length < 2) return;
    group.sort((a, b) => a.along - b.along);
    const fracs = edgeBiasedAlongFractions(group.length);
    group.forEach((item, i) => {
      const t = fracs[i];
      const cam = next[item.index];
      // 실측 0.1m 격자에 맞춘 뒤 정규좌표로 환산 → 도면·표 수치가 같게
      if (side === 'top' || side === 'bottom') {
        const alongM = Math.round(t * poolLengthM * 10) / 10;
        const xNorm = b.minX + (alongM / Math.max(poolLengthM, 0.01)) * spanX;
        cam.x = Math.round(xNorm * 10) / 10;
        cam.y = Math.round((side === 'top' ? b.minY : b.maxY) * 10) / 10;
      } else {
        const alongM = Math.round(t * poolWidthM * 10) / 10;
        const yNorm = b.minY + (alongM / Math.max(poolWidthM, 0.01)) * spanY;
        cam.x = Math.round((side === 'left' ? b.minX : b.maxX) * 10) / 10;
        cam.y = Math.round(yNorm * 10) / 10;
      }
    });
  });
  return next;
}

export type AimPose = { heading_deg: number; tilt_deg: number };

type AimPoseOptions = {
  installHeightM?: number;
  wallRows?: number;
  wallCols?: number;
  fillCoverage?: boolean;
};

/**
 * 현재 위치·높이·벽 거리로 팬·틸트만 다시 잡는다.
 * 좌표·렌즈는 바꾸지 않는다.
 */
export function aimPoseAfterPosition(
  cam: CameraSpec,
  cameras: CameraSpec[],
  poolLengthM: number,
  poolWidthM: number,
  options?: AimPoseOptions,
): AimPose {
  const p = xyMeters(cam, poolLengthM, poolWidthM);
  if (cam.mount_type === 'ceiling') {
    const deg = (Math.atan2(poolWidthM / 2 - p.y, poolLengthM / 2 - p.x) * 180) / Math.PI;
    return {
      heading_deg: Math.round((((deg % 360) + 360) % 360) * 10) / 10,
      tilt_deg: 90,
    };
  }
  const side = wallSideFromCamName(cam.name) || detectWallSide(p, poolLengthM, poolWidthM);
  const h = Math.max(0.5, Number(options?.installHeightM ?? cam.height_m) || 3.2);
  const wRows = (options?.wallRows ?? 0) > 0 ? options!.wallRows! : 1;
  const wCols = (options?.wallCols ?? 0) > 0 ? options!.wallCols! : 1;
  const edgeM = Number(cam.pool_edge_offset_m) || 0;
  const wallM = Number(cam.wall_mount_offset_m) || 0;
  const eff = effectiveWallXYM(p, poolLengthM, poolWidthM, edgeM, wallM, side);
  const fill = !!options?.fillCoverage;
  const vfov = Number(cam.fov_v_deg) || 50;

  if (fill && side !== 'inside') {
    const { row, col } = wallCellIndex(side, p, wRows, wCols, poolLengthM, poolWidthM);
    const alongIdx = side === 'top' || side === 'bottom' ? col : row;
    const alongCount = side === 'top' || side === 'bottom' ? wCols : wRows;
    const isCenter =
      isCenterAlongCell(alongIdx, alongCount) ||
      ((side === 'left' || side === 'right') && alongCount <= 2);
    const { tx, ty } = wallGroundAimPoint(
      side,
      col,
      wCols,
      row,
      wRows,
      poolLengthM,
      poolWidthM,
      eff,
    );
    if (activeWallCoverageProfile === 'balanced_20260722') {
      const counts = countWallSides(cameras, poolLengthM, poolWidthM);
      const hasCeiling = cameras.some((c) => c.mount_type === 'ceiling');
      const farFrac = balancedFarFracForSide(side, isCenter, counts, hasCeiling);
      const { nx, ny } = wallNearGroundPoint(side, poolLengthM, poolWidthM, tx, ty);
      const { fx, fy } = wallFarGroundPoint(
        side,
        farFrac,
        poolLengthM,
        poolWidthM,
        tx,
        ty,
      );
      const covered = poseCoveringPoolEdgeToFar(eff.x, eff.y, h, nx, ny, fx, fy, 68);
      return { heading_deg: covered.heading_deg, tilt_deg: covered.tilt_deg };
    }
    return poseTowardGroundTarget(eff.x, eff.y, h, tx, ty, vfov, true);
  }
  if (side !== 'inside') {
    const { row, col } = wallCellIndex(side, p, wRows, wCols, poolLengthM, poolWidthM);
    const { tx, ty } = wallGroundAimPoint(
      side,
      col,
      wCols,
      row,
      wRows,
      poolLengthM,
      poolWidthM,
      eff,
    );
    return poseTowardGroundTarget(eff.x, eff.y, h, tx, ty, vfov, false);
  }
  const intoDepth = wallCoverageSpanM(side, wRows, wCols, poolLengthM, poolWidthM).intoDepth;
  const autoTilt = wallTiltTowardDepthM(h, intoDepth);
  return {
    heading_deg: wallFacingHeadingDeg(side, p, poolLengthM, poolWidthM),
    tilt_deg: Math.round(
      Math.max(
        WALL_LABEL_TILT_MIN,
        Math.min(WALL_LABEL_TILT_MAX, (autoTilt + DEFAULT_WALL_TILT_DEG) / 2),
      ) * 10,
    ) / 10,
  };
}

/**
 * 설치 위치를 옮긴 뒤의 팬(°).
 * 자동 배치와 같은 조준(그 자리 앞 수면)만 다시 잡고, 틸트·렌즈는 바꾸지 않는다.
 */
export function headingDegAfterPositionMove(
  cam: CameraSpec,
  cameras: CameraSpec[],
  poolLengthM: number,
  poolWidthM: number,
  options?: AimPoseOptions,
): number {
  return aimPoseAfterPosition(cam, cameras, poolLengthM, poolWidthM, options).heading_deg;
}

/**
 * 위치 유지(또는 유동 간격 시 벽면 재배치). 각 CCTV 초점(mm)을 시작값으로,
 * 천장 격자는 셀 경계에 맞춰 겹치지 않게, 벽 등은 인접 거리 기준으로 화각 조정.
 */
export function autoOptimizeCameraSettings(
  cameras: CameraSpec[],
  poolLengthM: number,
  poolWidthM: number,
  options?: AutoOptimizeOptions,
): CameraSpec[] {
  if (!cameras.length) return cameras;

  const layoutDefaultFocal = Math.max(
    MIN_FOCAL_MM,
    Math.min(MAX_FOCAL_MM, Number(options?.defaultFocalMm) || DEFAULT_FOCAL_LENGTH_MM),
  );

  const grid = options?.ceilingGrid;
  const gridRows = grid?.rows ?? 0;
  const gridCols = grid?.cols ?? 0;
  const gridBounds = grid?.bounds;
  const cellHalf =
    gridBounds && gridRows > 0 && gridCols > 0
      ? cellHalfSpanM(gridBounds, gridRows, gridCols, poolLengthM, poolWidthM)
      : null;

  const wGrid = options?.wallGrid;
  const wGridRows = wGrid?.rows ?? 0;
  const wGridCols = wGrid?.cols ?? 0;
  const wGridBounds = wGrid?.bounds;

  const workingWall =
    options?.flexInstallSpacing && options?.fillCoverage
      ? redistributeWallInstallPositions(
          cameras,
          poolLengthM,
          poolWidthM,
          wGridBounds ?? gridBounds,
        )
      : cameras;

  const working =
    options?.flexInstallSpacing && options?.fillCoverage
      ? redistributeCeilingInstallPositions(
          workingWall,
          poolLengthM,
          poolWidthM,
          gridRows > 0 && gridCols > 0
            ? { rows: gridRows, cols: gridCols, bounds: gridBounds }
            : undefined,
        )
      : workingWall;

  const wallSideCounts = countWallSides(working, poolLengthM, poolWidthM);
  const hasCeilingCams = working.some((c) => c.mount_type !== 'wall');

  const fallbackDist = Math.min(poolLengthM, poolWidthM) / 3;

  return working.map((cam, i) => {
    const mount = cam.mount_type === 'wall' ? 'wall' : 'ceiling';
    const p = xyMeters(cam, poolLengthM, poolWidthM);
    const h = Math.max(0.5, Number(cam.height_m) || 4.5);
    const sensorW = Number(cam.sensor_width_mm) || DEFAULT_SENSOR_WIDTH_MM;
    const camFocal = Number(cam.focal_length_mm);
    // fillCoverage: 매회 레이아웃 기본 초점부터 재산정(이전 광각 결과를 다시 min 하면 범위가 누적 확대됨)
    // 그 외: 카메라에 지정된 초점 유지
    const defaultFocal = Math.max(
      MIN_FOCAL_MM,
      Math.min(
        MAX_FOCAL_MM,
        options?.fillCoverage
          ? layoutDefaultFocal
          : Number.isFinite(camFocal) && camFocal > 0
            ? camFocal
            : layoutDefaultFocal,
      ),
    );

    const opticsDefault = applyCameraOptics({
      mount_type: mount,
      focal_length_mm: defaultFocal,
      sensor_width_mm: sensorW,
      resolution_w: cam.resolution_w,
      resolution_h: cam.resolution_h,
    });

    const focalLocked = options?.lockedFocalIndices?.has(i);
    let focal = defaultFocal;
    let wallReachM: number | undefined;

    if (!focalLocked) {
      if (mount === 'ceiling' && cellHalf) {
        const rw = Number(cam.resolution_w) || 1920;
        const rh = Number(cam.resolution_h) || 1080;
        // fillCoverage: 셀 반폭이 아니라 셀 대각(구석)까지 덮는 화각
        const coverHalfW = options?.fillCoverage
          ? Math.hypot(cellHalf.halfW, cellHalf.halfH) * 1.05
          : cellHalf.halfW;
        const coverHalfH = options?.fillCoverage
          ? Math.hypot(cellHalf.halfW, cellHalf.halfH) * 1.05
          : cellHalf.halfH;
        const focalFit = focalForCellCoverage(
          coverHalfW,
          coverHalfH,
          h,
          sensorW,
          rw,
          rh,
        );

        if (options?.fillCoverage) {
          // 천장: 셀+구석까지 덮도록 약간 여유(1.22). 필요 시 기본 초점보다 광각 허용
          focal = Math.min(defaultFocal, focalFit);
          const halfNeed = Math.max(cellHalf.halfW, cellHalf.halfH) * 1.22;
          const cornerNeed = Math.hypot(cellHalf.halfW, cellHalf.halfH) * 1.08;
          const need = Math.max(halfNeed, cornerNeed);
          const reach = Math.hypot(h, need) * 1.1;
          wallReachM = Math.min(40, Math.max(8, reach));
          // 셀 대각이 기본 화각으로 부족하면 focalFit(더 광각) 유지 — 이미 min 적용됨
        } else {
          const rAtDefault = groundRadiusM(opticsDefault.fov_h_deg, h);
          const opticsFit = applyCameraOptics({
            mount_type: mount,
            focal_length_mm: focalFit,
            sensor_width_mm: sensorW,
            resolution_w: rw,
            resolution_h: rh,
          });
          const rFit = groundRadiusM(opticsFit.fov_h_deg, h);
          const halfNeed = Math.max(cellHalf.halfW, cellHalf.halfH);
          if (rAtDefault > halfNeed) {
            focal = Math.max(defaultFocal, focalFit);
          } else if (rAtDefault < rFit * 0.95) {
            focal = Math.min(defaultFocal, focalFit);
          } else {
            focal = defaultFocal;
          }
          wallReachM = Math.min(40, Math.max(8, Math.hypot(h, halfNeed * 1.1) * 1.05));
        }
      } else if (mount === 'ceiling') {
        // 격자 정보 없이도 천장캠 시야거리 유지
        wallReachM = Math.min(
          40,
          Math.max(12, Number(cam.max_range_m) || Math.hypot(h, Math.min(poolLengthM, poolWidthM) * 0.35)),
        );
      } else if (mount === 'wall') {
        const rw = Number(cam.resolution_w) || 1920;
        const rh = Number(cam.resolution_h) || 1080;
        const side = detectWallSide(p, poolLengthM, poolWidthM);
        const rows = wGridRows > 0 ? wGridRows : 1;
        const cols = wGridCols > 0 ? wGridCols : 1;
        const { intoDepth } = wallCoverageSpanM(side, rows, cols, poolLengthM, poolWidthM);
        const { row, col } = wallCellIndex(side, p, rows, cols, poolLengthM, poolWidthM);
        const { halfAlong: cellHalfAlong, halfInto: cellHalfInto } = wallCellFocalHalfM(
          side,
          col,
          cols,
          row,
          rows,
          poolLengthM,
          poolWidthM,
        );
        const seam =
          activeWallCoverageProfile === 'balanced_20260722' && options?.fillCoverage
            ? BALANCED_WALL_SEAM_OVERLAP
            : 1;
        const slant = Math.hypot(h, intoDepth * 0.98);
        wallReachM = Math.min(38, Math.max(10, slant * 1.05));

        // 벽캠: 천장처럼 높이로 화각을 잡으면 과광각→겹침 면적이 커짐.
        // 조준 수평거리 기준으로 담당 반폭을 덮는 H/V를 역산.
        let focalFit: number;
        if (
          activeWallCoverageProfile === 'balanced_20260722' &&
          options?.fillCoverage
        ) {
          // 조준 수평거리: 중앙=가운데, 측면=근거리
          const edgeM0 = Number(cam.pool_edge_offset_m) || 0;
          const wallM0 = Number(cam.wall_mount_offset_m) || 0;
          const gapM = wallCameraToPoolGapM(edgeM0, wallM0);
          const depthM =
            side === 'top' || side === 'bottom'
              ? poolWidthM
              : side === 'left' || side === 'right'
                ? poolLengthM
                : Math.min(poolLengthM, poolWidthM);
          const aimFrac = wallBalancedNearAimFrac(
            side,
            col,
            cols,
            row,
            rows,
            wallProfileParams().nearAimFrac,
          );
          const aimHorizFixed = Math.max(
            3.2,
            depthM * aimFrac + gapM,
          );
          // 풀 가장자리(근거리) 수평거리 — 좌·우 1대처럼 담담이 넓을 때 H/V를 여기서 맞춤
          const nearHoriz = Math.max(
            0.45,
            gapM + depthM * BALANCED_NEAR_EDGE_FRAC,
          );
          const halfA = cellHalfAlong * seam;
          const halfI = cellHalfInto * seam;
          let hfovDeg = (2 * Math.atan(halfA / aimHorizFixed) * 180) / Math.PI;
          let vfovDeg = (2 * Math.atan(halfI / aimHorizFixed) * 180) / Math.PI;
          // 가장자리부터 커버: 근거리에서 담당 반폭이 들어오도록 H 하한
          const hfovNear =
            (2 * Math.atan(halfA / nearHoriz) * 180) / Math.PI;
          hfovDeg = Math.max(hfovDeg, Math.min(125, hfovNear));
          // 근~원 깊이 V
          const farFracAim = balancedFarFracForSide(
            side,
            side === 'left' || side === 'right'
              ? true
              : isCenterAlongCell(
                  side === 'top' || side === 'bottom' ? col : row,
                  side === 'top' || side === 'bottom' ? cols : rows,
                ),
            wallSideCounts,
            hasCeilingCams,
          );
          const farHoriz = Math.max(
            nearHoriz + 0.5,
            depthM * farFracAim + gapM,
          );
          const angNearDeg = (Math.atan2(h, nearHoriz) * 180) / Math.PI;
          const angFarDeg = (Math.atan2(h, farHoriz) * 180) / Math.PI;
          vfovDeg = Math.max(vfovDeg, Math.max(18, angNearDeg - angFarDeg));
          hfovDeg = Math.max(28, Math.min(125, hfovDeg));
          vfovDeg = Math.max(20, Math.min(100, vfovDeg));
          const aspect = rh / Math.max(rw, 1);
          const hfovFromV =
            (2 * Math.atan(Math.tan((vfovDeg * Math.PI) / 180 / 2) / aspect) * 180) /
            Math.PI;
          focalFit = focalFromHorizontalFovDeg(
            Math.min(125, Math.max(hfovDeg, hfovFromV)),
            sensorW,
          );
        } else {
          focalFit = focalForCellCoverage(
            cellHalfAlong * seam,
            cellHalfInto * seam,
            h,
            sensorW,
            rw,
            rh,
          );
        }
        if (options?.fillCoverage) {
          if (wallProfileParams().wallPerCellFov) {
            focal = Math.max(MIN_FOCAL_MM, Math.min(MAX_FOCAL_MM, focalFit));
            if (activeWallCoverageProfile === 'balanced_20260722') {
              focal = Math.min(defaultFocal, focal);
            } else if (focalFit < defaultFocal) {
              focal = Math.min(defaultFocal, focalFit);
            }
          } else {
            focal = Math.min(defaultFocal, focalFit);
          }
        } else {
          focal = defaultFocal;
        }
      } else {
        let minDist = Infinity;
        for (let j = 0; j < cameras.length; j++) {
          if (i === j) continue;
          const q = xyMeters(cameras[j], poolLengthM, poolWidthM);
          const d = Math.hypot(p.x - q.x, p.y - q.y);
          if (d < minDist) minDist = d;
        }
        if (!Number.isFinite(minDist) || minDist < 0.15) {
          minDist = fallbackDist;
        }
        const halfAllowM = (minDist * FOOTPRINT_SEPARATION) / 2;
        let maxHfovDeg = (2 * Math.atan(halfAllowM / h) * 180) / Math.PI;
        maxHfovDeg = Math.max(22, Math.min(110, maxHfovDeg));
        const focalForSeparation = focalFromHorizontalFovDeg(maxHfovDeg, sensorW);
        focal = Math.max(defaultFocal, focalForSeparation);
      }

      focal = Math.max(MIN_FOCAL_MM, Math.min(MAX_FOCAL_MM, focal));
    } else {
      focal = Math.max(
        MIN_FOCAL_MM,
        Math.min(MAX_FOCAL_MM, Number(cam.focal_length_mm) || defaultFocal),
      );
    }

    const patch: Partial<CameraSpec> = {};
    // 초점·H/V는 항상 초점에서 파생. 고정 렌즈(locked)면 그 mm만 유지하고 이후 광각 확대 금지.
    {
      const rw = Number(cam.resolution_w) || 1920;
      const rh = Number(cam.resolution_h) || 1080;
      const lockedFocal = Math.max(
        MIN_FOCAL_MM,
        Math.min(MAX_FOCAL_MM, Number(cam.focal_length_mm) || defaultFocal),
      );
      const useFocal = focalLocked ? lockedFocal : focal;
      const o = applyCameraOptics({
        mount_type: mount,
        focal_length_mm: useFocal,
        sensor_width_mm: sensorW,
        resolution_w: rw,
        resolution_h: rh,
      });
      patch.focal_length_mm = Math.round(useFocal * 100) / 100;
      patch.fov_h_deg = o.fov_h_deg;
      patch.fov_v_deg = o.fov_v_deg;
      if (focalLocked) {
        focal = useFocal;
      }
    }
    if (wallReachM != null && !focalLocked) {
      patch.max_range_m = Math.round(wallReachM * 10) / 10;
    }

    if (mount === 'ceiling') {
      patch.tilt_deg = 90;
    } else {
      const side = detectWallSide(p, poolLengthM, poolWidthM);
      const rows = wGridRows > 0 ? wGridRows : 1;
      const cols = wGridCols > 0 ? wGridCols : 1;
      const { intoDepth } = wallCoverageSpanM(side, rows, cols, poolLengthM, poolWidthM);
      const edgeM = Number(cam.pool_edge_offset_m) || 0;
      const wallM = Number(cam.wall_mount_offset_m) || 0;
      const namedSide = wallSideFromCamName(cam.name);
      const sideForOpt = namedSide || side;
      const eff = effectiveWallXYM(
        p,
        poolLengthM,
        poolWidthM,
        edgeM,
        wallM,
        sideForOpt,
      );
      const opticsNow = applyCameraOptics({
        mount_type: 'wall',
        focal_length_mm: Number(patch.focal_length_mm ?? focal),
        sensor_width_mm: sensorW,
        resolution_w: Number(cam.resolution_w) || 1920,
        resolution_h: Number(cam.resolution_h) || 1080,
      });
      const vfov = Number(patch.fov_v_deg ?? opticsNow.fov_v_deg);

        if (options?.fillCoverage && side !== 'inside') {
        const { row, col } = wallCellIndex(side, p, rows, cols, poolLengthM, poolWidthM);
        const alongIdx = side === 'top' || side === 'bottom' ? col : row;
        const alongCount = side === 'top' || side === 'bottom' ? cols : rows;
        // 좌·우 1~2대: 중앙 취급(맞은편 이음 + 근거리 가장자리)
        const isCenter =
          isCenterAlongCell(alongIdx, alongCount) ||
          ((side === 'left' || side === 'right') && alongCount <= 2);
        const { tx, ty } = wallGroundAimPoint(
          side,
          col,
          cols,
          row,
          rows,
          poolLengthM,
          poolWidthM,
          eff,
        );

        if (activeWallCoverageProfile === 'balanced_20260722') {
          const farFrac = balancedFarFracForSide(
            side,
            isCenter,
            wallSideCounts,
            hasCeilingCams,
          );
          const { nx, ny } = wallNearGroundPoint(
            side,
            poolLengthM,
            poolWidthM,
            tx,
            ty,
          );
          const { fx, fy } = wallFarGroundPoint(
            side,
            farFrac,
            poolLengthM,
            poolWidthM,
            tx,
            ty,
          );
          const soleSide =
            !hasCeilingCams &&
            ((side === 'left' && wallSideCounts.right <= 0) ||
              (side === 'right' && wallSideCounts.left <= 0) ||
              (side === 'top' && wallSideCounts.bottom <= 0) ||
              (side === 'bottom' && wallSideCounts.top <= 0));
          const vCap = soleSide
            ? Math.max(78, wallProfileParams().maxWallVfovDeg ?? 62)
            : side === 'left' || side === 'right'
              ? Math.max(72, wallProfileParams().maxWallVfovDeg ?? 62)
              : isCenter
                ? Math.max(70, wallProfileParams().maxWallVfovDeg ?? 62)
                : wallProfileParams().maxWallVfovDeg ?? 62;
          const covered = poseCoveringPoolEdgeToFar(
            eff.x,
            eff.y,
            h,
            nx,
            ny,
            fx,
            fy,
            vCap,
          );
          patch.heading_deg = covered.heading_deg;
          patch.tilt_deg = covered.tilt_deg;
          patch.max_range_m = covered.max_range_m;
          // 틸트는 covered.V 기준이므로 실제 V도 반드시 맞춤(안 맞으면 가장자리 공백)
          // 고정 렌즈면 V/H/초점을 넓히지 않음 — 위치·팬·틸트만으로 커버
          const curV = Number(patch.fov_v_deg ?? vfov);
          if (!focalLocked && covered.vfov_deg > curV + 0.05) {
            const o = applyCameraOptics({
              mount_type: 'wall',
              fov_v_deg: covered.vfov_deg,
              sensor_width_mm: sensorW,
              resolution_w: Number(cam.resolution_w) || 1920,
              resolution_h: Number(cam.resolution_h) || 1080,
            });
            patch.focal_length_mm = o.focal_length_mm;
            patch.fov_h_deg = o.fov_h_deg;
            patch.fov_v_deg = o.fov_v_deg;
          }
        } else {
          const pose = poseTowardGroundTarget(eff.x, eff.y, h, tx, ty, vfov, true);
          patch.heading_deg = pose.heading_deg;
          patch.tilt_deg = pose.tilt_deg;
          patch.max_range_m =
            Math.round(
              wallMaxRangeFromGroundAim(
                eff.x,
                eff.y,
                h,
                tx,
                ty,
                vfov,
                poolLengthM,
                poolWidthM,
              ) * 10,
            ) / 10;
        }
      } else if (side !== 'inside') {
        const { row, col } = wallCellIndex(side, p, rows, cols, poolLengthM, poolWidthM);
        const { tx, ty } = wallGroundAimPoint(
          side,
          col,
          cols,
          row,
          rows,
          poolLengthM,
          poolWidthM,
          eff,
        );
        const pose = poseTowardGroundTarget(eff.x, eff.y, h, tx, ty, vfov, false);
        patch.heading_deg = pose.heading_deg;
        patch.tilt_deg = pose.tilt_deg;
      } else {
        patch.heading_deg = wallFacingHeadingDeg(side, p, poolLengthM, poolWidthM);
        const autoTilt = wallTiltTowardDepthM(h, intoDepth);
        patch.tilt_deg =
          Math.round(
            Math.max(
              WALL_LABEL_TILT_MIN,
              Math.min(WALL_LABEL_TILT_MAX, (autoTilt + DEFAULT_WALL_TILT_DEG) / 2),
            ) * 10,
          ) / 10;
      }
    }

    if (mount === 'wall' && options?.fillCoverage) {
      const pr = wallProfileParams();
      const sideCap = detectWallSide(p, poolLengthM, poolWidthM);
      const wRows = wGridRows > 0 ? wGridRows : 1;
      const wCols = wGridCols > 0 ? wGridCols : 1;
      const cellIdx = wallCellIndex(sideCap, p, wRows, wCols, poolLengthM, poolWidthM);
      const edgeW = wallCellEdgeWeight(
        sideCap,
        cellIdx.col,
        wCols,
        cellIdx.row,
        wRows,
      );

      if (pr.maxRangeScale < 1 && patch.max_range_m != null) {
        let r = Number(patch.max_range_m) * pr.maxRangeScale;
        if (pr.minWallRangeM > 0) {
          r = Math.max(pr.minWallRangeM, r);
        }
        patch.max_range_m = Math.round(r * 10) / 10;
      }
      if (pr.neighborFovCap && edgeW < 0.45) {
        // 균형안: 이웃 H 상한이 V를 깎아 풀 가장자리(근거리)가 비는 문제 → 적용 안 함
        // (셀 seam·담당 반폭으로 이미 겹침 관리)
        const applyNeighborCap =
          activeWallCoverageProfile !== 'balanced_20260722';
        if (applyNeighborCap) {
        let minDm = Infinity;
        for (let j = 0; j < cameras.length; j++) {
          if (i === j) continue;
          if (cameras[j].mount_type !== 'wall') continue;
          const q = xyMeters(cameras[j], poolLengthM, poolWidthM);
          // 같은 벽면만 (좌·우를 같은 벽으로 오인하지 않음)
          if (detectWallSide(q, poolLengthM, poolWidthM) !== sideCap) continue;
          const d = Math.hypot(p.x - q.x, p.y - q.y);
          if (d < minDm) minDm = d;
        }
        if (!focalLocked && Number.isFinite(minDm) && minDm > 0.25) {
          const sep = FOOTPRINT_SEPARATION;
          const half = (minDm * sep) / 2;
          let capH = (2 * Math.atan(half / h) * 180) / Math.PI;
          capH = Math.max(24, Math.min(110, capH));
          const minFocal = focalFromHorizontalFovDeg(capH, sensorW);
          const curF = Number(patch.focal_length_mm ?? focal);
          if (curF < minFocal) {
            focal = Math.min(MAX_FOCAL_MM, minFocal);
            const o = applyCameraOptics({
              mount_type: 'wall',
              focal_length_mm: focal,
              sensor_width_mm: sensorW,
              resolution_w: Number(cam.resolution_w) || 1920,
              resolution_h: Number(cam.resolution_h) || 1080,
            });
            patch.focal_length_mm = o.focal_length_mm;
            patch.fov_h_deg = o.fov_h_deg;
            patch.fov_v_deg = o.fov_v_deg;
          }
        }
        }
      }
      let hFovCap = pr.maxWallHfovDeg;
      if (
        hFovCap != null &&
        pr.maxWallHfovDegEdge != null &&
        pr.maxWallHfovDegEdge > hFovCap
      ) {
        hFovCap = hFovCap + (pr.maxWallHfovDegEdge - hFovCap) * edgeW;
      }
      // 화각 상한은 초점 하한으로만 적용 (H/V를 따로 깎지 않음)
      // 균형안 벽캠: 근거리 가장자리용 광각을 H/V 상한으로 다시 조이지 않음
      // 고정 렌즈: 초점·H/V 변경 금지
      const skipHfovTighten =
        focalLocked || activeWallCoverageProfile === 'balanced_20260722';
      if ((hFovCap != null || pr.maxWallVfovDeg != null) && !skipHfovTighten) {
        let minFocalNeeded = MIN_FOCAL_MM;
        if (hFovCap != null) {
          minFocalNeeded = Math.max(
            minFocalNeeded,
            focalFromHorizontalFovDeg(hFovCap, sensorW),
          );
        }
        if (pr.maxWallVfovDeg != null) {
          const rw = Number(cam.resolution_w) || 1920;
          const rh = Number(cam.resolution_h) || 1080;
          minFocalNeeded = Math.max(
            minFocalNeeded,
            focalFromVerticalFovDeg(pr.maxWallVfovDeg, sensorW, rw, rh),
          );
        }
        const curF = Number(patch.focal_length_mm ?? focal);
        if (curF < minFocalNeeded) {
          const o = applyCameraOptics({
            mount_type: 'wall',
            focal_length_mm: Math.min(MAX_FOCAL_MM, minFocalNeeded),
            sensor_width_mm: sensorW,
            resolution_w: Number(cam.resolution_w) || 1920,
            resolution_h: Number(cam.resolution_h) || 1080,
          });
          patch.focal_length_mm = o.focal_length_mm;
          patch.fov_h_deg = o.fov_h_deg;
          patch.fov_v_deg = o.fov_v_deg;
        } else {
          // 초점은 유지하고 H/V만 재파생(일관성)
          const o = applyCameraOptics({
            mount_type: 'wall',
            focal_length_mm: curF,
            sensor_width_mm: sensorW,
            resolution_w: Number(cam.resolution_w) || 1920,
            resolution_h: Number(cam.resolution_h) || 1080,
          });
          patch.focal_length_mm = o.focal_length_mm;
          patch.fov_h_deg = o.fov_h_deg;
          patch.fov_v_deg = o.fov_v_deg;
        }
      }

      // 화각 확정 후: 풀 가장자리~중앙선 커버 틸트·시야 재적용
      if (
        activeWallCoverageProfile === 'balanced_20260722' &&
        sideCap !== 'inside'
      ) {
        const alongIdx =
          sideCap === 'top' || sideCap === 'bottom' ? cellIdx.col : cellIdx.row;
        const alongCount =
          sideCap === 'top' || sideCap === 'bottom' ? wCols : wRows;
        const isCenter =
          isCenterAlongCell(alongIdx, alongCount) ||
          ((sideCap === 'left' || sideCap === 'right') && alongCount <= 2);
        const edgeMc = Number(cam.pool_edge_offset_m) || 0;
        const wallMc = Number(cam.wall_mount_offset_m) || 0;
        const effC = effectiveWallXYM(
          p,
          poolLengthM,
          poolWidthM,
          edgeMc,
          wallMc,
          sideCap,
        );
        const { tx, ty } = wallGroundAimPoint(
          sideCap,
          cellIdx.col,
          wCols,
          cellIdx.row,
          wRows,
          poolLengthM,
          poolWidthM,
          effC,
        );
        const farFrac = balancedFarFracForSide(
          sideCap,
          isCenter,
          wallSideCounts,
          hasCeilingCams,
        );
        const { nx, ny } = wallNearGroundPoint(
          sideCap,
          poolLengthM,
          poolWidthM,
          tx,
          ty,
        );
        const { fx, fy } = wallFarGroundPoint(
          sideCap,
          farFrac,
          poolLengthM,
          poolWidthM,
          tx,
          ty,
        );
        const soleSide =
          !hasCeilingCams &&
          ((sideCap === 'left' && wallSideCounts.right <= 0) ||
            (sideCap === 'right' && wallSideCounts.left <= 0) ||
            (sideCap === 'top' && wallSideCounts.bottom <= 0) ||
            (sideCap === 'bottom' && wallSideCounts.top <= 0));
        // 위·아래 중앙도 가장자리 우선 — V 하한을 충분히
        const sideVFloor = soleSide
          ? 78
          : sideCap === 'left' || sideCap === 'right'
            ? 72
            : isCenter
              ? 70
              : 62;
        const vCap = Math.max(
          Number(patch.fov_v_deg) || sideVFloor,
          pr.maxWallVfovDeg ?? 62,
          sideVFloor,
        );
        const covered = poseCoveringPoolEdgeToFar(
          effC.x,
          effC.y,
          h,
          nx,
          ny,
          fx,
          fy,
          vCap,
        );
        patch.heading_deg = covered.heading_deg;
        patch.tilt_deg = covered.tilt_deg;
        patch.max_range_m = covered.max_range_m;
        const curV = Number(patch.fov_v_deg) || 40;
        if (!focalLocked && covered.vfov_deg > curV + 0.05) {
          const o = applyCameraOptics({
            mount_type: 'wall',
            fov_v_deg: covered.vfov_deg,
            sensor_width_mm: sensorW,
            resolution_w: Number(cam.resolution_w) || 1920,
            resolution_h: Number(cam.resolution_h) || 1080,
          });
          patch.focal_length_mm = o.focal_length_mm;
          patch.fov_h_deg = o.fov_h_deg;
          patch.fov_v_deg = o.fov_v_deg;
        }
      }
    }

    return { ...cam, ...patch };
  });
}
